import { NativeModules, Platform } from "react-native";
import { check, PERMISSIONS, RESULTS } from "react-native-permissions";

/**
 * Thin wrapper over the native face-capture screen
 * (live.citymall.partnerdb.facecapture.FaceCaptureModule).
 *
 * The module ships in every binary from the native face-capture release
 * onward. `isNativeFaceCaptureAvailable()` only detects this JS bundle running
 * on an older APK — a deployment mistake the entry component reports to
 * Sentry, since there is no RN screen to fall back to any more.
 */

/**
 * `uploaded`: photo accepted by the backend. `closed`: the DB closed it (only
 * possible with canClose). `dismissed`: the backend withdrew the request
 * (`faceCaptureVisible` went false) and JS asked the screen to leave.
 */
export type NativeFaceCaptureStatus = "uploaded" | "closed" | "dismissed";

/** Live changes JS can push into a running session; only present keys apply. */
export interface NativeFaceCaptureUpdate {
  canClose?: boolean;
  text?: string;
}

export interface NativeFaceCaptureResult {
  status: NativeFaceCaptureStatus;
}

/** Exactly what FaceCaptureModule.launch() reads — see FaceCaptureConfig.kt. */
export interface NativeFaceCaptureConfig {
  text: string;
  canClose: boolean;
  flow: string;
  token: string;
  baseUrl: string;
  appVersion: string;
  appVersionCode: string;
  /**
   * Minimum blur score (Laplacian variance on the face crop) the captured
   * photo must clear. `-1` = native client default, `0` = gate off, `>0` =
   * threshold. Mirrors `face_capture.blurMinScore` when the backend sends it.
   */
  blurMinScore: number;
  /** `"auto"` (default) starts the capture by itself once the face is good; `"manual"` shows a shutter. */
  captureMode: NativeFaceCaptureMode;
  /**
   * Largest head angle in degrees (yaw, pitch or roll) accepted live and in
   * the still. `-1` = native default, `0` = gate off. Mirrors
   * `face_capture.maxHeadAngle` when the backend sends it.
   */
  maxHeadAngle: number;
}

export type NativeFaceCaptureMode = "auto" | "manual";

/** The `auth.face_capture` slice as the backend and authReducer shape it. */
export interface FaceCaptureReduxState {
  faceCaptureVisible?: boolean;
  canCloseFaceCapture?: boolean;
  faceCaptureText?: unknown;
  faceCaptureFlow?: unknown;
  isFaceCaptureUploading?: boolean;
  blurMinScore?: unknown;
  captureMode?: unknown;
  maxHeadAngle?: unknown;
}

interface FaceCaptureNativeModule {
  launch(config: NativeFaceCaptureConfig): Promise<NativeFaceCaptureResult>;
  update(changes: NativeFaceCaptureUpdate): void;
  dismiss(): void;
}

const nativeModule: FaceCaptureNativeModule | undefined =
  NativeModules?.FaceCaptureModule;

/** Module rejection code when React has no foreground activity to launch from. */
export const E_NO_ACTIVITY = "E_NO_ACTIVITY";

/** Module rejection code for anything the activity itself refused to run with. */
export const E_FACE_CAPTURE = "E_FACE_CAPTURE";

/** The activity's reason when it was launched without the CAMERA permission. */
export const CAMERA_PERMISSION_DENIED = "camera_permission_denied";

/**
 * The app's boot-time PermissionsGate owns the CAMERA permission; the native
 * screen never prompts. Check before launching so a missing permission means
 * "wait for the gate", not "open a screen that immediately closes".
 */
export const hasCameraPermission = async (): Promise<boolean> => {
  if (Platform.OS !== "android") return false;
  try {
    return (await check(PERMISSIONS.ANDROID.CAMERA)) === RESULTS.GRANTED;
  } catch {
    return false;
  }
};

export const isNativeFaceCaptureAvailable = (): boolean =>
  Platform.OS === "android" && typeof nativeModule?.launch === "function";

export const NATIVE_DEFAULT_BLUR_MIN_SCORE = -1;

/** A finite, non-negative number passes through; anything else defers to the native default. */
const asThreshold = (value: unknown): number =>
  typeof value === "number" && Number.isFinite(value) && value >= 0
    ? value
    : NATIVE_DEFAULT_BLUR_MIN_SCORE;

/** Only an explicit "manual" opts out of auto capture. */
const asCaptureMode = (value: unknown): NativeFaceCaptureMode =>
  value === "manual" ? "manual" : "auto";

const asString = (value: unknown): string => {
  if (typeof value === "string") return value;
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  return "";
};

/**
 * Pure mapping from Redux + config onto the native contract. Kept separate
 * from the launch so it is unit-testable without the bridge.
 */
export const buildNativeFaceCaptureConfig = (input: {
  faceCapture: FaceCaptureReduxState;
  token: unknown;
  baseUrl: unknown;
  appVersion: unknown;
  appVersionCode: unknown;
}): NativeFaceCaptureConfig => ({
  text: asString(input.faceCapture.faceCaptureText).trim(),
  canClose: Boolean(input.faceCapture.canCloseFaceCapture),
  flow: asString(input.faceCapture.faceCaptureFlow),
  token: asString(input.token),
  baseUrl: asString(input.baseUrl),
  appVersion: asString(input.appVersion),
  appVersionCode: asString(input.appVersionCode),
  blurMinScore: asThreshold(input.faceCapture.blurMinScore),
  captureMode: asCaptureMode(input.faceCapture.captureMode),
  maxHeadAngle: asThreshold(input.faceCapture.maxHeadAngle),
});

const errorCode = (error: unknown): unknown =>
  typeof error === "object" && error !== null
    ? (error as { code?: unknown }).code
    : undefined;

const errorText = (error: unknown): unknown =>
  typeof error === "object" && error !== null
    ? (error as { message?: unknown }).message
    : undefined;

export const isNoActivityError = (error: unknown): boolean =>
  errorCode(error) === E_NO_ACTIVITY;

/** The activity bailed because CAMERA is not granted — a "not yet", not a failure. */
export const isCameraPermissionError = (error: unknown): boolean =>
  errorCode(error) === E_FACE_CAPTURE &&
  errorText(error) === CAMERA_PERMISSION_DENIED;

/**
 * Forward a Redux change to the running screen. Redux stays the source of
 * truth for the whole session, as it was for the RN modal: a later backend
 * payload that flips `canCloseFaceCapture` or `faceCaptureText` applies at
 * once. Safe to call when nothing is running.
 */
export const updateNativeFaceCapture = (
  changes: NativeFaceCaptureUpdate,
): void => {
  nativeModule?.update?.(changes);
};

/** The backend cleared `faceCaptureVisible`; the screen leaves unless an upload is in flight. */
export const dismissNativeFaceCapture = (): void => {
  nativeModule?.dismiss?.();
};

export const launchNativeFaceCapture = (
  config: NativeFaceCaptureConfig,
): Promise<NativeFaceCaptureResult> => {
  if (!nativeModule?.launch) {
    return Promise.reject(new Error("FaceCaptureModule unavailable"));
  }
  return nativeModule.launch(config);
};
